/**
 * Page de gestion des séjours (ADMIN UNIQUEMENT)
 * CRUD complet : Créer, Modifier, Supprimer, Activer/Désactiver
 */
import React, { useState, useEffect } from 'react';
import { sejourService, partenaireService } from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import { confirm } from '../components/ConfirmContainer';
import logger from '../utils/logger';

const GestionSejours = () => {
  const [sejours, setSejours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSejour, setEditingSejour] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    lieu: '',
    date_debut: '',
    date_fin: '',
    age_min: '',
    age_max: '',
    places_totales: '',
    prix: '',
    lien_paiement_helloasso: '',
    actif: true,
    inscriptions_ouvertes: true,
  });
  const [tranches, setTranches] = useState([]);
  const [pagePresentation, setPagePresentation] = useState(null);
  const [pagePresentationUrl, setPagePresentationUrl] = useState('');
  const [allPartenaires, setAllPartenaires] = useState([]);
  const [selectedPartenaires, setSelectedPartenaires] = useState([]);

  useEffect(() => {
    loadSejours();
    loadPartenaires();
  }, []);

  const loadPartenaires = async () => {
    try {
      const response = await partenaireService.getAll();
      // L'API peut retourner soit un tableau direct, soit un objet avec results
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      logger.log('Partenaires chargés:', data);
      setAllPartenaires(data);
    } catch (error) {
      logger.error('Erreur lors du chargement des partenaires:', error);
      notify.error('Erreur lors du chargement des partenaires');
    }
  };

  const loadSejours = async () => {
    try {
      setLoading(true);
      const response = await sejourService.getAll();
      const sejoursData = response.data.results || response.data;
      setSejours(Array.isArray(sejoursData) ? sejoursData : []);
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement des séjours');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (sejour = null) => {
    if (sejour) {
      logger.log('=== OUVERTURE MODAL EDITION ===');
      logger.log('Séjour complet:', sejour);
      logger.log('page_presentation:', sejour.page_presentation);
      setEditingSejour(sejour);
      setFormData({
        titre: sejour.titre || '',
        description: sejour.description || '',
        lieu: sejour.lieu || '',
        date_debut: sejour.date_debut || '',
        date_fin: sejour.date_fin || '',
        age_min: sejour.age_min !== null && sejour.age_min !== undefined ? sejour.age_min : '',
        age_max: sejour.age_max !== null && sejour.age_max !== undefined ? sejour.age_max : '',
        places_totales: sejour.places_totales !== null && sejour.places_totales !== undefined ? sejour.places_totales : '',
        prix: sejour.prix !== null && sejour.prix !== undefined ? sejour.prix : '',
        lien_paiement_helloasso: sejour.lien_paiement_helloasso || '',
        actif: sejour.actif !== undefined ? sejour.actif : true,
        inscriptions_ouvertes: sejour.inscriptions_ouvertes !== undefined ? sejour.inscriptions_ouvertes : true,
      });
      setTranches(sejour.tranches_prix || []);
      setPagePresentation(null);
      // Ajouter /media/ si nécessaire
      const pageUrl = sejour.page_presentation ?
        (sejour.page_presentation.startsWith('/media/') || sejour.page_presentation.startsWith('http')
          ? sejour.page_presentation
          : `/media/${sejour.page_presentation}`)
        : '';
      logger.log('Page presentation URL:', pageUrl, 'Raw:', sejour.page_presentation);
      setPagePresentationUrl(pageUrl);
      // Charger les partenaires du séjour
      setSelectedPartenaires((sejour.partenaires || []).map(p => p.id));
    } else {
      setEditingSejour(null);
      setFormData({
        titre: '',
        description: '',
        lieu: '',
        date_debut: '',
        date_fin: '',
        age_min: '',
        age_max: '',
        places_totales: '',
        prix: '',
        lien_paiement_helloasso: '',
        actif: true,
        inscriptions_ouvertes: true,
      });
      setTranches([]);
      setPagePresentation(null);
      setPagePresentationUrl('');
      setSelectedPartenaires([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSejour(null);
    setTranches([]);
    setPagePresentation(null);
    setPagePresentationUrl('');
    setSelectedPartenaires([]);
  };

  const handleAddTranche = () => {
    setTranches([...tranches, { age_min: '', age_max: '', prix: '', places_disponibles: '', places_partenaires: 0 }]);
  };

  const handleRemoveTranche = (index) => {
    setTranches(tranches.filter((_, i) => i !== index));
  };

  const handleTrancheChange = (index, field, value) => {
    const updatedTranches = [...tranches];
    updatedTranches[index][field] = value;
    setTranches(updatedTranches);
  };

  const handlePartenaireToggle = (partenaireId) => {
    setSelectedPartenaires(prev => {
      if (prev.includes(partenaireId)) {
        return prev.filter(id => id !== partenaireId);
      } else {
        return [...prev, partenaireId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // DEBUG: Logger les tranches avant envoi
      console.log('=== DEBUG SUBMIT ===');
      console.log('Tranches état:', tranches);
      console.log('Tranches avec IDs:', tranches.map(t => ({ id: t.id, age_min: t.age_min, age_max: t.age_max })));

      // Si on a un fichier, utiliser FormData
      if (pagePresentation) {
        const formDataToSend = new FormData();

        // Ajouter tous les champs du formulaire
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== '') {
            formDataToSend.append(key, formData[key]);
          }
        });

        // Ajouter le fichier HTML
        formDataToSend.append('page_presentation', pagePresentation);

        // Ajouter les tranches de prix si elles existent
        if (tranches.length > 0) {
          // Supprimer prix et places_totales s'ils sont vides
          if (!formData.prix || formData.prix === '') {
            formDataToSend.delete('prix');
          }
          if (!formData.places_totales || formData.places_totales === '') {
            formDataToSend.delete('places_totales');
          }

          formDataToSend.append('tranches_prix', JSON.stringify(tranches.map(t => ({
            id: t.id || null, // Envoyer l'ID si la tranche existe déjà
            age_min: parseInt(t.age_min),
            age_max: parseInt(t.age_max),
            prix: parseFloat(t.prix),
            places_disponibles: parseInt(t.places_disponibles),
            places_partenaires: parseInt(t.places_partenaires || 0)
          }))));
        }

        let sejourId;
        if (editingSejour) {
          await sejourService.updateWithFile(editingSejour.id, formDataToSend);
          sejourId = editingSejour.id;
          notify.success('Séjour modifié avec succès');
        } else {
          const response = await sejourService.createWithFile(formDataToSend);
          sejourId = response.data.id;
          notify.success('Séjour créé avec succès');
        }

        // Mettre à jour les partenaires
        await sejourService.updatePartenaires(sejourId, selectedPartenaires);
      } else {
        // Pas de fichier, envoyer en JSON classique
        const dataToSend = { ...formData };

        // Si on a des tranches, ne pas envoyer prix et places_totales s'ils sont vides
        if (tranches.length > 0) {
          if (!dataToSend.prix || dataToSend.prix === '') {
            delete dataToSend.prix;
          }
          if (!dataToSend.places_totales || dataToSend.places_totales === '') {
            delete dataToSend.places_totales;
          }

          dataToSend.tranches_prix = tranches.map(t => ({
            id: t.id || null, // Envoyer l'ID si la tranche existe déjà
            age_min: parseInt(t.age_min),
            age_max: parseInt(t.age_max),
            prix: parseFloat(t.prix),
            places_disponibles: parseInt(t.places_disponibles),
            places_partenaires: parseInt(t.places_partenaires || 0)
          }));
        }

        let sejourId;
        if (editingSejour) {
          await sejourService.update(editingSejour.id, dataToSend);
          sejourId = editingSejour.id;
          notify.success('Séjour modifié avec succès');
        } else {
          const response = await sejourService.create(dataToSend);
          sejourId = response.data.id;
          notify.success('Séjour créé avec succès');
        }

        // Mettre à jour les partenaires
        await sejourService.updatePartenaires(sejourId, selectedPartenaires);
      }

      await loadSejours();
      handleCloseModal();
    } catch (error) {
      logger.error('Erreur lors de l\'enregistrement:', error);
      logger.error('Détails de l\'erreur:', error.response?.data);
      const errorMsg = error.response?.data
        ? JSON.stringify(error.response.data, null, 2)
        : 'Erreur inconnue';
      notify.error('Erreur lors de l\'enregistrement du séjour:\n' + errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (await confirm.delete('Êtes-vous sûr de vouloir supprimer ce séjour ?', 'Supprimer le séjour')) {
      try {
        await sejourService.delete(id);
        notify.success('Séjour supprimé avec succès');
        await loadSejours();
      } catch (error) {
        logger.error('Erreur lors de la suppression:', error);
        notify.error('Erreur lors de la suppression du séjour');
      }
    }
  };

  const handleToggleActif = async (sejour) => {
    try {
      await sejourService.toggleActif(sejour.id);
      notify.success(`Séjour ${sejour.actif ? 'désactivé' : 'activé'} avec succès`);
      await loadSejours();
    } catch (error) {
      logger.error('Erreur lors du changement de statut:', error);
      notify.error('Erreur lors du changement de statut');
    }
  };

  const handleArchiver = async (sejour) => {
    // Vérifier d'abord si le séjour peut être archivé
    try {
      const verificationResponse = await sejourService.verifierArchivable(sejour.id);

      if (!verificationResponse.data.archivable) {
        notify.error(verificationResponse.data.message);
        return;
      }

      // Demander confirmation avec informations détaillées
      const confirmMessage = `⚠️ ATTENTION - Action irréversible ⚠️\n\n` +
        `${verificationResponse.data.message}\n\n` +
        `Cette action va :\n` +
        `✓ Supprimer toutes les données personnelles (RGPD)\n` +
        `✓ Supprimer les documents et emails\n` +
        `✓ Conserver uniquement : nom/prénom enfants, données financières\n\n` +
        `Cette action est DÉFINITIVE et ne peut pas être annulée.\n\n` +
        `Voulez-vous vraiment archiver ce séjour ?`;

      if (await confirm.delete(confirmMessage, 'Archiver le séjour')) {
        const response = await sejourService.archiver(sejour.id);

        notify.success(
          `Séjour archivé avec succès.\n` +
          `${response.data.resultat.stats.inscriptions_archivees} inscription(s) traitée(s).\n` +
          `${response.data.resultat.stats.documents_supprimes} document(s) supprimé(s).`
        );

        await loadSejours();
      }
    } catch (error) {
      logger.error('Erreur lors de l\'archivage:', error);
      const errorMsg = error.response?.data?.error || 'Erreur lors de l\'archivage du séjour';
      notify.error(errorMsg);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est un fichier HTML
      if (file.type !== 'text/html' && !file.name.endsWith('.html')) {
        notify.warning('Veuillez sélectionner un fichier HTML (.html)');
        e.target.value = '';
        return;
      }
      setPagePresentation(file);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Séjours</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">Administration complète des séjours de vacances</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-4 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            Créer un séjour
          </button>
        </div>

        {sejours.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
            <p className="text-sm sm:text-base text-gray-500">Aucun séjour créé</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-4 text-xs sm:text-sm text-primary-600 hover:text-primary-700 active:text-primary-800 transition-colors"
            >
              Créer le premier séjour
            </button>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 overflow-hidden rounded-md">
            <ul className="divide-y divide-gray-200">
              {sejours.map((sejour) => (
                <li key={sejour.id} className="hover:bg-gray-50 active:bg-gray-100 transition-colors">
                  <div className="px-3 py-3 sm:px-4 sm:py-4 lg:px-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{sejour.titre}</h3>
                          {sejour.archive ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-700 text-white border-2 border-gray-500">
                              📦 ARCHIVÉ
                            </span>
                          ) : sejour.actif ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Actif
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactif
                            </span>
                          )}
                          {!sejour.archive && sejour.inscriptions_ouvertes === false && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              📅 Inscriptions bientôt
                            </span>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-500">
                          <p>📍 {sejour.lieu}</p>
                          <p>📅 {new Date(sejour.date_debut).toLocaleDateString('fr-FR')} - {new Date(sejour.date_fin).toLocaleDateString('fr-FR')}</p>
                          <p>👥 {sejour.age_min}-{sejour.age_max} ans</p>
                          <p>💰 {sejour.prix} €</p>
                          <p>🪑 Places: {sejour.places_restantes}/{sejour.places_totales}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {sejour.page_presentation ? (
                          <a
                            href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${sejour.id}/presentation/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-green-600 hover:text-green-900 transition-colors"
                            title="Voir la page de présentation"
                          >
                            🌐
                            <span className="text-sm">Page</span>
                          </a>
                        ) : (
                          <span
                            className="flex items-center gap-1 text-gray-400 cursor-not-allowed"
                            title="Aucune page de présentation"
                          >
                            🌐
                            <span className="text-sm">Page</span>
                          </span>
                        )}
                        <button
                          onClick={() => handleToggleActif(sejour)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-900 transition-colors"
                          title={sejour.actif ? 'Désactiver' : 'Activer'}
                        >
                          {sejour.actif ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                        {!sejour.archive && (
                          <>
                            <button
                              onClick={() => handleOpenModal(sejour)}
                              className="flex items-center gap-1 text-primary-600 hover:text-primary-900 transition-colors"
                            >
                              <PencilIcon className="h-5 w-5" />
                              Modifier
                            </button>
                            <button
                              onClick={() => handleArchiver(sejour)}
                              className="flex items-center gap-1 text-orange-600 hover:text-orange-900 transition-colors"
                              title="Archiver (RGPD)"
                            >
                              <ArchiveBoxIcon className="h-5 w-5" />
                              Archiver
                            </button>
                            <button
                              onClick={() => handleDelete(sejour.id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-900 transition-colors"
                            >
                              <TrashIcon className="h-5 w-5" />
                              Supprimer
                            </button>
                          </>
                        )}
                        {sejour.archive && (
                          <span className="text-sm text-gray-500 italic">
                            Séjour archivé le {new Date(sejour.date_archivage).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Modal de création/édition */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSejour ? 'Modifier le séjour' : 'Créer un séjour'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Section Informations générales */}
                <div className="bg-gray-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Informations générales</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Titre *</label>
                      <input
                        type="text"
                        name="titre"
                        required
                        value={formData.titre}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        name="description"
                        rows="3"
                        value={formData.description || ''}
                        onChange={handleChange}
                        placeholder="Décrivez le séjour (optionnel)..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Lieu *</label>
                      <input
                        type="text"
                        name="lieu"
                        required
                        value={formData.lieu}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="flex items-center h-full pt-7">
                        <input
                          type="checkbox"
                          name="actif"
                          checked={formData.actif}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Séjour actif (visible pour les parents)</span>
                      </label>
                    </div>

                    <div>
                      <label className="flex items-center h-full pt-7">
                        <input
                          type="checkbox"
                          name="inscriptions_ouvertes"
                          checked={formData.inscriptions_ouvertes}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Inscriptions ouvertes</span>
                      </label>
                      <p className="mt-1 text-xs text-gray-500 ml-6">Décocher pour afficher le séjour sans permettre les inscriptions</p>
                    </div>
                  </div>
                </div>

                {/* Section Dates et âges */}
                <div className="bg-blue-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Dates et tranches d'âge</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date de début *</label>
                      <input
                        type="date"
                        name="date_debut"
                        required
                        value={formData.date_debut}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date de fin *</label>
                      <input
                        type="date"
                        name="date_fin"
                        required
                        value={formData.date_fin}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Âge minimum *</label>
                      <input
                        type="number"
                        name="age_min"
                        required
                        min="3"
                        value={formData.age_min}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Âge maximum *</label>
                      <input
                        type="number"
                        name="age_max"
                        required
                        min="3"
                        value={formData.age_max}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section Prix et places (global) */}
                <div className="bg-green-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Prix et places (global)</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {tranches.length > 0
                      ? "Ces champs sont optionnels si vous utilisez des tranches de prix."
                      : "Si vous n'utilisez pas de tranches de prix, ces valeurs seront appliquées à tous les participants."}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Prix de base (€) {tranches.length === 0 && '*'}
                      </label>
                      <input
                        type="number"
                        name="prix"
                        required={tranches.length === 0}
                        step="0.01"
                        min="0"
                        value={formData.prix}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Places totales {tranches.length === 0 && '*'}
                      </label>
                      <input
                        type="number"
                        name="places_totales"
                        required={tranches.length === 0}
                        min="1"
                        value={formData.places_totales}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section Page de présentation HTML */}
                <div className="bg-purple-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Page de présentation personnalisée (optionnel)</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Uploadez un fichier HTML avec CSS et JS pour créer une page de présentation stylée du séjour.
                    Utilisez {'{sejour.titre}'}, {'{sejour.description}'}, etc. pour insérer les données dynamiquement.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        📄 Fichier HTML
                      </label>
                      <input
                        type="file"
                        accept=".html,text/html"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary-50 file:text-primary-700
                        hover:file:bg-primary-100
                        cursor-pointer"
                      />
                    </div>

                    {/* Aperçu du fichier actuel */}
                    {pagePresentationUrl && !pagePresentation && (
                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Fichier actuel :</strong>
                        </p>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${process.env.REACT_APP_API_URL.replace('/api', '')}${pagePresentationUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 text-sm flex items-center gap-1"
                          >
                            📎 Voir le fichier
                          </a>
                          <span className="text-gray-400">|</span>
                          <a
                            href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${editingSejour?.id}/presentation/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                          >
                            🌐 Aperçu de la page
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Aperçu du nouveau fichier sélectionné */}
                    {pagePresentation && (
                      <div className="bg-green-50 p-3 rounded-md border border-green-200">
                        <p className="text-sm text-green-800">
                          <strong>✓ Nouveau fichier sélectionné :</strong> {pagePresentation.name}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Ce fichier sera uploadé lors de la sauvegarde
                        </p>
                      </div>
                    )}

                    {!pagePresentationUrl && !pagePresentation && (
                      <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-md">
                        <p className="text-sm text-gray-500 italic">
                          Aucune page de présentation pour ce séjour
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Consultez le fichier GUIDE_CREATION_PAGE_SEJOUR.md pour créer votre page
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Paiement en ligne Hello Asso */}
                <div className="bg-pink-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">💳 Paiement en ligne Hello Asso</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Lien vers le formulaire de paiement Hello Asso (ex: lien adhésion). Les parents verront un bouton "Payer" qui ouvrira ce lien.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔗 Lien Hello Asso (optionnel)
                    </label>
                    <input
                      type="url"
                      name="lien_paiement_helloasso"
                      value={formData.lien_paiement_helloasso || ''}
                      onChange={handleChange}
                      placeholder="https://www.helloasso.com/associations/..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Collez ici l'URL complète de votre formulaire de paiement Hello Asso
                    </p>

                    {formData.lien_paiement_helloasso && (
                      <div className="mt-2 flex items-center gap-2">
                        <a
                          href={formData.lien_paiement_helloasso}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
                        >
                          🔗 Tester le lien
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Partenaires */}
                <div className="bg-indigo-50 p-4 rounded-md mb-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Partenaires sociaux</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Sélectionnez les partenaires sociaux autorisés à inscrire des enfants à ce séjour
                  </p>

                  {Array.isArray(allPartenaires) && allPartenaires.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {allPartenaires.map((partenaire) => (
                        <label
                          key={partenaire.id}
                          className="flex items-center p-3 bg-white border rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPartenaires.includes(partenaire.id)}
                            onChange={() => handlePartenaireToggle(partenaire.id)}
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                          />
                          <span className="ml-3 text-sm text-gray-900">{partenaire.nom}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-md">
                      <p className="text-sm text-gray-500 italic">
                        {!Array.isArray(allPartenaires)
                          ? 'Chargement des partenaires...'
                          : 'Aucun partenaire créé. Vous devrez d\'abord créer des partenaires dans la gestion des partenaires.'
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Section Tranches de prix */}
                <div className="bg-yellow-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900">Tranches de prix par âge (optionnel)</h4>
                      <p className="text-xs text-gray-600 mt-1">Définissez des prix différents selon l'âge avec un nombre de places limité par tranche.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddTranche}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Ajouter une tranche
                    </button>
                  </div>

                  {tranches.length > 0 && (
                    <div className="space-y-3">
                      {tranches.map((tranche, index) => {
                        // Une tranche existante (créée) a un ID et ne peut voir ses âges modifiés
                        const isExistingTranche = editingSejour && tranche.id;
                        const ageFieldsDisabled = isExistingTranche;
                        const disabledTooltip = "Les âges ne peuvent pas être modifiés après création. Seuls le prix et les places sont modifiables.";

                        return (
                        <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-md">
                          <div className="flex-1 relative">
                            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              Âge min
                              {ageFieldsDisabled && (
                                <span className="text-gray-400" title={disabledTooltip}>
                                  🔒
                                </span>
                              )}
                            </label>
                            <input
                              type="number"
                              value={tranche.age_min}
                              onChange={(e) => handleTrancheChange(index, 'age_min', e.target.value)}
                              min="3"
                              required
                              disabled={ageFieldsDisabled}
                              title={ageFieldsDisabled ? disabledTooltip : ''}
                              className={`w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
                                ageFieldsDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>
                          <div className="flex-1 relative">
                            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              Âge max
                              {ageFieldsDisabled && (
                                <span className="text-gray-400" title={disabledTooltip}>
                                  🔒
                                </span>
                              )}
                            </label>
                            <input
                              type="number"
                              value={tranche.age_max}
                              onChange={(e) => handleTrancheChange(index, 'age_max', e.target.value)}
                              min="3"
                              required
                              disabled={ageFieldsDisabled}
                              title={ageFieldsDisabled ? disabledTooltip : ''}
                              className={`w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
                                ageFieldsDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Prix (€)</label>
                            <input
                              type="number"
                              value={tranche.prix}
                              onChange={(e) => handleTrancheChange(index, 'prix', e.target.value)}
                              step="0.01"
                              min="0"
                              required
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Places normales
                              <span className="block text-xs font-normal text-gray-500 mt-0.5">
                                (hors partenaires)
                              </span>
                            </label>
                            <input
                              type="number"
                              value={tranche.places_disponibles}
                              onChange={(e) => handleTrancheChange(index, 'places_disponibles', e.target.value)}
                              min="0"
                              required
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Places partenaires
                              <span className="block text-xs font-normal text-gray-500 mt-0.5">
                                (en plus des normales)
                              </span>
                            </label>
                            <input
                              type="number"
                              value={tranche.places_partenaires || 0}
                              onChange={(e) => handleTrancheChange(index, 'places_partenaires', e.target.value)}
                              min="0"
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTranche(index)}
                            className="mt-6 text-red-600 hover:text-red-800"
                            title="Supprimer cette tranche"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {tranches.length === 0 && (
                    <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-md">
                      <p className="text-sm text-gray-500 italic">
                        Aucune tranche de prix définie. Le prix unique du séjour sera utilisé pour tous les participants.
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Cliquez sur "Ajouter une tranche" pour créer des tarifs personnalisés par âge.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    {editingSejour ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionSejours;
